"use client"

import Image from "next/image"
import { ApertureStyles, T, MAXW, SectionEyebrow, ArrowR } from "./_shared"

export default function ApertureFounder() {
  return (
    <section id="founder" className="relative scroll-mt-24" style={{ background: T.bgSoft }}>
      <ApertureStyles />
      <div className={`${MAXW} mx-auto px-6 lg:px-10 py-12 sm:py-16 md:py-24`}>
        <div className="max-w-[720px] mx-auto">
          <SectionEyebrow center>From the founder</SectionEyebrow>
          <h2
            className="mt-3 leading-[1.05] tracking-[-0.035em] text-center"
            style={{ fontSize: "clamp(1.9rem, 3.6vw, 2.8rem)", fontWeight: 800 }}
          >
            Built from a{" "}
            <em style={{ color: T.acc, fontStyle: "italic", fontWeight: 700 }}>real frustration.</em>
          </h2>

          <article
            className="mt-12 rounded-2xl p-8 sm:p-10"
            style={{
              background: T.surface,
              border: `1px solid ${T.hair}`,
              boxShadow: `0 1px 0 rgba(255,255,255,0.6) inset, 0 24px 64px -32px rgba(10,10,10,0.18)`,
            }}
          >
            <header className="flex items-center gap-4 pb-6 mb-6 border-b" style={{ borderColor: T.hair }}>
              <div
                className="relative flex-shrink-0 overflow-hidden"
                style={{
                  width: 64, height: 64, borderRadius: 999,
                  background: T.bgSoft,
                  border: `1px solid ${T.hair}`,
                  boxShadow: `0 14px 28px -18px rgba(10,10,10,0.35)`,
                }}
              >
                <Image
                  src="/founder-damian.png"
                  alt="Illustrated portrait of Damian Forzani"
                  className="object-cover"
                  width={64}
                  height={64}
                  style={{ width: "100%", height: "100%" }}
                  priority
                />
              </div>
              <div className="leading-tight min-w-0 flex-1">
                <p style={{ fontSize: 14.5, fontWeight: 700, color: T.ink, letterSpacing: "-0.01em" }}>
                  Damian Forzani
                </p>
                <p className="mt-1" style={{ fontSize: 12.5, color: T.ink3 }}>
                  Founder, DataBridge
                </p>
              </div>
              <span
                className="hidden sm:inline-flex ml-auto items-center gap-1.5 px-2.5 py-1 rounded-full flex-shrink-0"
                style={{
                  background: T.accChip,
                  border: `1px solid rgba(15,81,50,0.25)`,
                  color: T.acc,
                  fontSize: 11, fontWeight: 600,
                }}
              >
                <span style={{ width: 5, height: 5, background: T.acc, borderRadius: 999, animation: "apl-pulse 2s infinite" }} />
                Built by hand
              </span>
            </header>

            {/* One body scale, one line-height, ONE COLOR. The
                previous version mixed ink (dark) intro + ink2 (grey)
                body + ink blockquote + ink2 closing, which read as
                four tones of draft. Founder messages are personal
                letters — uniform dark ink throughout makes the voice
                feel consistent. Emphasis comes from weight (the
                'Eight validation checks' span) and from the
                blockquote's italic + left-border rule, not from
                color shifts. */}
            <div className="space-y-5" style={{ fontSize: 16, color: T.ink, lineHeight: 1.7 }}>
              <p style={{ fontWeight: 500 }}>
                DataBridge started as a personal challenge. I wanted to prove to myself
                that I could build a real product — something useful, careful, and complete.
              </p>
              <p>
                But the idea came from something I had lived myself: messy email lists,
                broken addresses, duplicate rows, columns that didn&rsquo;t match, and tools
                that charged in credits while still leaving me guessing what actually happened.
              </p>
              <blockquote
                className="my-7 pl-5"
                style={{ borderLeft: `2px solid ${T.acc}`, fontStyle: "italic" }}
              >
                So I built the tool I wish I had.
              </blockquote>
              <p>
                Upload a CSV, let DataBridge catch the risky emails, explain every flag,
                map the export, compare files when needed, and give you back something
                clean enough to trust.
              </p>
              <p>
                It&rsquo;s a small tool on the surface, but it was built with a big obsession:
                making data cleanup feel less like fighting a spreadsheet — and more like
                getting your work back under control.
              </p>
            </div>

            <footer className="mt-9 pt-6 border-t flex flex-wrap items-center gap-3" style={{ borderColor: T.hair }}>
              <a
                href="/changelog"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg transition-all"
                style={{
                  background: T.bgSoft,
                  border: `1px solid ${T.hair}`,
                  color: T.ink,
                  fontSize: 13, fontWeight: 500,
                }}
              >
                See the changelog <ArrowR />
              </a>
              <a
                href="mailto:founder@databridge.so"
                className="inline-flex items-center gap-2 px-3 py-2.5 transition-colors"
                style={{ color: T.ink2, fontSize: 13, fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
              >
                founder@databridge.so
              </a>
            </footer>
          </article>
        </div>
      </div>
    </section>
  )
}
